/**
 * Build the survey-kit registry the funnel runtime hands to `<SurveyStep>`.
 *
 * Starts from `buildDefaultRegistry()` (every renderer survey-kit ships)
 * and applies brand-agnostic overrides — `account_create` and `user_sync`
 * route to Kinde or Clerk implementations based on application config.
 */
import { defineRegistry, buildDefaultRegistry } from "@nextday/survey-kit";
import type { StepRenderer, StepRendererRegistry } from "@nextday/survey-kit";

import { accountCreateOverride } from "@src/funnel/accountCreateOverride";
import { userSyncOverride } from "@src/funnel/userSyncOverride";

const isOverridden = (
  renderer: StepRenderer<unknown>,
  overrides: ReadonlyArray<StepRenderer<unknown>>,
): boolean => overrides.some((override) => override.type === renderer.type);

const collectDefaults = (
  defaults: StepRendererRegistry,
  overrides: ReadonlyArray<StepRenderer<unknown>>,
): StepRenderer<unknown>[] => {
  const result: StepRenderer<unknown>[] = [];
  for (const renderer of Object.values(defaults)) {
    if (renderer && !isOverridden(renderer, overrides)) result.push(renderer);
  }
  return result;
};

export const buildFunnelRegistry = (): StepRendererRegistry => {
  const overrides: ReadonlyArray<StepRenderer<unknown>> = [
    accountCreateOverride as StepRenderer<unknown>,
    userSyncOverride as StepRenderer<unknown>,
  ];
  const defaults = buildDefaultRegistry();
  return defineRegistry([...collectDefaults(defaults, overrides), ...overrides]);
};

export default buildFunnelRegistry;
